import { useState } from "react";
import { useContainerWidth, symmetricExtent } from "./flowsChartShared";

/**
 * The PEAD signature visual (2026-10-27): a sector's pooled high-SUE-
 * quintile MINUS low-SUE-quintile cumulative return, trading days 1->60
 * after earnings entry. Hand-rolled SVG (ValuationBandChart.tsx's own
 * precedent) -- a single line + a zero reference + a hover crosshair,
 * nothing react-financial-charts is built for.
 *
 * DESCRIPTIVE ONLY: this line has no significance test of its own -- the
 * REAL verdict (relevant/raw_only/noise/insufficient_history) comes from
 * the cohort/HAC/BH-FDR pipeline shown in the results matrix above this
 * chart. A null sector's pooled curve can still wiggle from ordinary
 * noise (verified directly: several NOISE-verdict sectors show a
 * non-trivial day-60 reading here) -- the caller is responsible for
 * stating the test verdict next to every curve; this component never
 * renders one itself.
 */

export interface DriftCurvePoint {
  day: number;
  diff: number | null;
  nHigh: number;
  nLow: number;
}

const HEIGHT = 220;
const MARGIN = { top: 14, right: 20, bottom: 26, left: 44 };

export function DriftCurveChart({
  points, nHighTotal, nLowTotal,
}: {
  points: DriftCurvePoint[];
  nHighTotal: number;
  nLowTotal: number;
}) {
  const { ref, width } = useContainerWidth<HTMLDivElement>();
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  if (points.length < 2) {
    return <div ref={ref} className="empty-state" style={{ minHeight: 80 }}>Not enough resolved events for a curve.</div>;
  }
  if (width === 0) {
    return <div ref={ref} style={{ minHeight: HEIGHT }} />;
  }

  const plotW = Math.max(width - MARGIN.left - MARGIN.right, 10);
  const plotH = HEIGHT - MARGIN.top - MARGIN.bottom;

  const [yMin, yMax] = symmetricExtent(points.map((p) => p.diff), 0.2, 0.005);
  const xAt = (i: number) => MARGIN.left + (i / (points.length - 1)) * plotW;
  const yAt = (v: number) => MARGIN.top + (1 - (v - yMin) / (yMax - yMin)) * plotH;
  const zeroY = yAt(0);

  // Split into contiguous segments so a None (frontier-truncated day)
  // creates a real gap rather than a misleading straight line across it.
  const segments: string[] = [];
  let current = "";
  points.forEach((p, i) => {
    if (p.diff === null) {
      if (current) segments.push(current);
      current = "";
      return;
    }
    current += `${current ? "L" : "M"}${xAt(i).toFixed(1)},${yAt(p.diff).toFixed(1)} `;
  });
  if (current) segments.push(current);

  const tickIdx = [0, Math.round((points.length - 1) * 0.25), Math.round((points.length - 1) * 0.5), Math.round((points.length - 1) * 0.75), points.length - 1];

  function onMove(e: React.MouseEvent<SVGSVGElement>) {
    const rect = (e.target as SVGSVGElement).getBoundingClientRect();
    const x = e.clientX - rect.left;
    const frac = (x - MARGIN.left) / plotW;
    const idx = Math.max(0, Math.min(points.length - 1, Math.round(frac * (points.length - 1))));
    setHoverIdx(idx);
  }

  const hp = hoverIdx !== null ? points[hoverIdx] : null;

  return (
    <div ref={ref}>
      <svg
        width={width}
        height={HEIGHT}
        role="img"
        aria-label="PEAD drift curve"
        onMouseMove={onMove}
        onMouseLeave={() => setHoverIdx(null)}
      >
        {/* Zero reference line. */}
        <line x1={MARGIN.left} x2={MARGIN.left + plotW} y1={zeroY} y2={zeroY} stroke="#8a91a0" strokeWidth={1} strokeDasharray="4 3" />
        <text x={MARGIN.left - 6} y={zeroY + 3} fontSize={9} fill="#8a91a0" textAnchor="end">0%</text>

        {/* Y-axis top/bottom labels. */}
        <text x={MARGIN.left - 6} y={MARGIN.top + 4} fontSize={9} fill="#8a91a0" textAnchor="end">{(yMax * 100).toFixed(1)}%</text>
        <text x={MARGIN.left - 6} y={MARGIN.top + plotH} fontSize={9} fill="#8a91a0" textAnchor="end">{(yMin * 100).toFixed(1)}%</text>

        {/* The drift curve itself. */}
        {segments.map((d, i) => (
          <path key={i} d={d} fill="none" stroke="#3467c9" strokeWidth={1.8} />
        ))}

        {/* Hover crosshair + dot. */}
        {hp && (
          <>
            <line x1={xAt(hoverIdx!)} x2={xAt(hoverIdx!)} y1={MARGIN.top} y2={MARGIN.top + plotH} stroke="#8a91a0" strokeWidth={1} strokeDasharray="2 2" opacity={0.6} />
            {hp.diff !== null && <circle cx={xAt(hoverIdx!)} cy={yAt(hp.diff)} r={3.5} fill="#3467c9" stroke="#fff" strokeWidth={1} />}
          </>
        )}

        {/* X-axis tick labels (trading day number). */}
        {tickIdx.map((i) => (
          <text key={i} x={xAt(i)} y={HEIGHT - 8} fontSize={9} fill="#8a91a0" textAnchor="middle">
            day {points[i].day}
          </text>
        ))}
      </svg>
      <div className="drift-hover-line" style={{ fontSize: "0.72rem", marginTop: 2, minHeight: "1.1em" }}>
        {hp ? (
          hp.diff !== null ? (
            <>
              <b>day {hp.day}</b>: {hp.diff >= 0 ? "+" : ""}{(hp.diff * 100).toFixed(2)}% (high-SUE minus low-SUE) —
              n = {hp.nHigh} high / {hp.nLow} low
            </>
          ) : (
            <>day {hp.day}: not yet resolved (past the current data frontier for enough events)</>
          )
        ) : (
          <>hover the chart for an exact day's reading</>
        )}
      </div>
      <div className="drift-static-note" style={{ fontSize: "0.72rem", color: "var(--ink-faint)" }}>
        quintile size: {nHighTotal} high-SUE / {nLowTotal} low-SUE events, pooled across this sector's full history.
      </div>
    </div>
  );
}
